import React, { useMemo } from 'react';
import ReactLinkify from 'react-linkify';
import './MetaThreadDisplay.css';

// Utility function from react-threads
const formatToRelative = (timestamp) => {
  if (typeof timestamp === 'undefined') {
    return '-';
  }
  const timestampMs = timestamp * 1000;
  const seconds = Math.floor((new Date().getTime() - timestampMs) / 1000);
  
  let interval = seconds / 31536000;
  if (interval > 1) {
    return Math.floor(interval) + 'y';
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return Math.floor(interval) + 'm';
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return Math.floor(interval) + 'd';
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return Math.floor(interval) + 'h';
  }
  interval = seconds / 60;
  if (interval > 1) {
    return Math.floor(interval) + 'm';
  }
  return Math.floor(seconds) + 's';
};

// Exact ThreadsIcons from the repo
const ThreadsIcons = {
  Verified: (props) => (
    <svg
      aria-label="Verified"
      color="rgb(0, 149, 246)"
      fill="rgb(0, 149, 246)"
      height="12"
      role="img"
      viewBox="0 0 40 40"
      width="12"
      style={{ position: 'relative', display: 'block' }}
      {...props}
    >
      <title>Verified</title>
      <path
        d="M19.998 3.094 14.638 0l-2.972 5.15H5.432v6.354L0 14.64 3.094 20 0 25.359l5.432 3.137v5.905h5.975L14.638 40l5.36-3.094L25.358 40l3.232-5.6h6.162v-6.01L40 25.359 36.905 20 40 14.641l-5.248-3.03v-6.46h-6.419L25.358 0l-5.36 3.094Zm7.415 11.225 2.254 2.287-11.43 11.5-6.835-6.93 2.244-2.258 4.587 4.581 9.18-9.18Z"
        fillRule="evenodd"
      />
    </svg>
  ),
  Like: (props) => (
    <svg
      aria-label="Like"
      color="rgb(243, 245, 247)"
      fill="transparent"
      height="19"
      role="img"
      viewBox="0 0 24 22"
      width="20"
      {...props}
    >
      <title>Like</title>
      <path
        d="M1 7.66c0 4.575 3.899 9.086 9.987 12.934.338.203.74.406 1.013.406.283 0 .686-.203 1.013-.406C19.1 16.746 23 12.234 23 7.66 23 3.736 20.245 1 16.672 1 14.603 1 12.98 1.94 12 3.352 11.042 1.952 9.408 1 7.328 1 3.766 1 1 3.736 1 7.66Z"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  ),
  Comment: (props) => (
    <svg
      aria-label="Comment"
      color="rgb(243, 245, 247)"
      fill="rgb(243, 245, 247)"
      height="20"
      role="img"
      viewBox="0 0 24 24"
      width="20"
      {...props}
    >
      <title>Comment</title>
      <path
        d="M20.656 17.008a9.993 9.993 0 1 0-3.59 3.615L22 22Z"
        fill="none"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  ),
  Repost: (props) => (
    <svg
      aria-label="Repost"
      color="rgb(243, 245, 247)"
      fill="rgb(243, 245, 247)"
      height="20"
      role="img"
      viewBox="0 0 24 24"
      width="20"
      {...props}
    >
      <title>Repost</title>
      <path d="M19.998 9.497a1 1 0 0 0-1 1v4.228a3.274 3.274 0 0 1-3.27 3.27h-5.313l1.791-1.787a1 1 0 0 0-1.412-1.416L7.29 18.287a1.004 1.004 0 0 0-.294.707v.001c0 .023.012.042.013.065a.923.923 0 0 0 .281.643l3.502 3.504a1 1 0 0 0 1.414-1.414l-1.797-1.798h5.318a5.276 5.276 0 0 0 5.27-5.27v-4.228a1 1 0 0 0-1-1Zm-6.41-3.496-1.795 1.795a1 1 0 1 0 1.414 1.414l3.5-3.5a1.003 1.003 0 0 0 0-1.417l-3.5-3.5a1 1 0 0 0-1.414 1.414l1.794 1.794H8.27A5.277 5.277 0 0 0 3 9.271V13.5a1 1 0 0 0 2 0V9.271a3.275 3.275 0 0 1 3.271-3.27Z" />
    </svg>
  ),
  Send: (props) => (
    <svg
      aria-label="Share"
      color="rgb(243, 245, 247)"
      fill="rgb(243, 245, 247)"
      height="20"
      role="img"
      viewBox="0 0 24 24"
      width="20"
      {...props}
    >
      <title>Share</title>
      <line
        fill="none"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="2"
        x1="22"
        x2="9.218"
        y1="3"
        y2="10.083"
      />
      <polygon
        fill="none"
        points="11.698 20.334 22 3.001 2 3.001 9.218 10.084 11.698 20.334"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  ),
};

// Linkify wrapper component
const LinkifyWrapper = ({ children }) => {
  if (typeof window !== 'undefined') {
    return (
      <ReactLinkify
        componentDecorator={(decoratedHref, decoratedText, key) => (
          <a target="_blank" href={decoratedHref} key={key} style={{ color: 'rgb(0, 149, 246)' }}>
            {decoratedText}
          </a>
        )}
      >
        {children}
      </ReactLinkify>
    );
  }
  return children;
};

// Thread Image Component
const ThreadImage = ({ post }) => {
  const [contentImageError, setContentImageError] = React.useState(false);

  const bestCandidate = useMemo(() => {
    const candidates = post.image_versions2?.candidates || [];

    if (!candidates.length) {
      return null;
    }

    // Get largest candidate
    const best = candidates.reduce((prev, current) => {
      if ((prev?.width || 0) > (current?.width || 0)) {
        return prev;
      } else {
        return current;
      }
    }, undefined);

    return best;
  }, [post.image_versions2]);

  const getContentProxyImageUrl = (originalUrl) => {
    if (!originalUrl) return null;
    return `/.netlify/functions/image-proxy?url=${encodeURIComponent(originalUrl)}`;
  };

  if (!bestCandidate) {
    return null;
  }

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ zIndex: 0, display: 'flex', minHeight: 0 }}>
        {!contentImageError ? (
          <img
            width={bestCandidate.width}
            height={bestCandidate.height}
            style={{
              width: '100%',
              height: 'auto',
              border: '0.5px solid rgba(243,245,247,0.15)',
              borderRadius: '8px',
              aspectRatio: `${post.original_width / post.original_height}`,
            }}
            alt=""
            src={getContentProxyImageUrl(bestCandidate.url)}
            crossOrigin="anonymous"
            onError={() => {
              console.log('Content image failed to load');
              setContentImageError(true);
            }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '200px',
            border: '0.5px solid rgba(243,245,247,0.15)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.05)',
            color: 'rgb(97,97,97)',
            fontSize: '14px'
          }}>
            📷 Image unavailable
          </div>
        )}
      </div>
    </div>
  );
};

// Button component
const Button = ({ children }) => {
  return (
    <div style={{
      display: 'flex',
      padding: '8px',
      marginLeft: '-7px',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div>{children}</div>
    </div>
  );
};

// Main Thread component with exact styling from react-threads
const MetaThreadDisplay = ({ threadData }) => {
  const [imageError, setImageError] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);

  if (!threadData?.post) {
    return null;
  }

  const post = threadData.post;
  const user = post.user;

  // Function to handle profile image loading with our Netlify proxy
  const getProxyImageUrl = (originalUrl) => {
    if (!originalUrl) return null;
    
    // Use our Netlify function as the primary proxy
    const netlifyProxy = `/.netlify/functions/image-proxy?url=${encodeURIComponent(originalUrl)}`;
    
    return netlifyProxy;
  };

  return (
    <div style={{
      padding: '12px 12px 16px',
      backgroundColor: 'rgb(10, 10, 10)'
    }}>
      {/* wrapper */}
      <div style={{
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '600px',
        margin: '0 auto',
        color: 'rgb(243, 245, 247)'
      }}>
        {/* content */}
        <div>
          <div style={{ paddingTop: '4px', paddingBottom: '16px' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '48px minmax(0, 1fr)',
              gridTemplateRows: '36px 0 max-content max-content'
            }}>
              
              {/* Avatar */}
              <div style={{
                paddingTop: 0,
                gridRow: '1 / span 2',
                gridColumn: '1'
              }}>
                                 <div style={{
                   width: '36px',
                   height: '36px',
                   borderRadius: '50%',
                   backgroundColor: 'rgb(30, 30, 30)',
                   overflow: 'hidden',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center'
                 }}>
                   {!!user && user.profile_pic_url && !imageError ? (
                     <img
                       style={{ 
                         width: '100%', 
                         height: '100%', 
                         objectFit: 'cover',
                         display: imageLoaded ? 'block' : 'none'
                       }}
                       alt=""
                       src={getProxyImageUrl(user.profile_pic_url)}
                       crossOrigin="anonymous"
                       onLoad={() => {
                         setImageLoaded(true);
                         setImageError(false);
                       }}
                       onError={() => {
                         console.log('Proxy failed, trying direct URL...');
                         setImageError(true);
                       }}
                     />
                   ) : null}
                   
                   {/* Fallback avatar when image fails or is loading */}
                   {(!user?.profile_pic_url || imageError || !imageLoaded) && (
                     <div style={{
                       width: '100%',
                       height: '100%',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                       color: '#ffffff',
                       fontSize: '14px',
                       fontWeight: '700'
                     }}>
                       {user?.username?.[0]?.toUpperCase() || '?'}
                     </div>
                   )}
                 </div>
              </div>
              
              {/* Header */}
              <div style={{
                alignSelf: 'center',
                paddingTop: 0,
                gridColumn: '2',
                gridRow: '1 / span 2'
              }}>
                <div style={{
                  display: 'grid',
                  height: '100%',
                  gridTemplateColumns: '1fr max-content'
                }}>
                  <div style={{ gridColumn: '1', alignSelf: 'center' }}>
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      <a style={{
                        color: 'rgb(243, 245, 247)',
                        display: 'inline',
                        textDecoration: 'none',
                        fontWeight: 600
                      }}>
                        {user?.username}
                      </a>
                      {user?.is_verified && (
                        <span style={{ marginLeft: '4px' }}>
                          <ThreadsIcons.Verified />
                        </span>
                      )}
                    </span>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    lineHeight: '20px',
                    gridColumn: '2'
                  }}>
                    <span style={{
                      color: 'rgb(97, 97, 97)',
                      minWidth: '24px',
                      textAlign: 'center',
                      display: 'inline-block',
                      fontSize: '15px'
                    }}>
                      {formatToRelative(post?.taken_at)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div style={{
                paddingTop: '10px',
                gridRow: '2 / span 2',
                gridColumn: '1 / span 2'
              }}>
                
                {/* Text Content */}
                <LinkifyWrapper>
                  {!!post?.caption?.text && (
                    <div>
                      <div style={{
                        marginTop: '3px',
                        whiteSpace: 'pre-wrap',
                        lineHeight: '140%',
                        overflowWrap: 'anywhere',
                        fontSize: '15px'
                      }}>
                        <p style={{ margin: 0, color: 'rgb(243, 245, 247)' }}>
                          {post.caption?.text}
                        </p>
                      </div>
                    </div>
                  )}
                </LinkifyWrapper>

                {/* Images */}
                {post && <ThreadImage post={post} />}

                {/* Action Buttons */}
                <div style={{ marginTop: '6px' }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '36px 36px 36px 36px'
                  }}>
                    <Button>
                      <ThreadsIcons.Like />
                    </Button>
                    <Button>
                      <ThreadsIcons.Comment />
                    </Button>
                    <Button>
                      <ThreadsIcons.Repost />
                    </Button>
                    <Button>
                      <ThreadsIcons.Send />
                    </Button>
                  </div>
                </div>

                {/* Stats */}
                <div style={{
                  display: 'flex',
                  alignItems: 'end',
                  gridRow: '4',
                  gridColumn: '1 / span 2'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '22px',
                    lineHeight: '21px',
                    fontSize: '15px',
                    color: 'rgb(97, 97, 97)'
                  }}>
                    {(post?.text_post_app_info?.direct_reply_count || 0) >= 1 && (
                      <span>
                        {`${post?.text_post_app_info?.direct_reply_count?.toLocaleString()} ${
                          post?.text_post_app_info?.direct_reply_count === 1 ? 'reply' : 'replies'
                        }`}
                      </span>
                    )}
                    {(post?.text_post_app_info?.direct_reply_count || 0) >= 1 && (post?.like_count || 0) >= 1 && (
                      <span>&nbsp;·&nbsp;</span>
                    )}
                    {(post?.like_count || 0) >= 1 && (
                      <span>{post?.like_count?.toLocaleString()} likes</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetaThreadDisplay; 